// src/wiring/installWiring.ts
//
// Canonical wiring installer (v1)
// -------------------------------
// One place to:
// - resolve entitlement
// - select install set (tier gated)
// - install capability manifests (side effects)
// - emit ops events + wiring-ready (via installCapabilities spine)
//
// Call once (AppShell recommended).
//

import { report } from "@/wiring/telemetry/report";

import type { Entitlement } from "@/wiring/entitlements/EntitlementResolver";
import { resolveEntitlement } from "@/wiring/entitlements/EntitlementResolver";

import { getAllManifests } from "@/wiring/manifest";
import type { CapabilityManifest, CapabilityTier } from "@/contracts/capabilities/CapabilityManifest";

import { installCapabilities } from "@/wiring/installCapabilities";
import type { CapabilityDescriptorV1, InstallContextV1 } from "@/wiring/installCapabilities";

export type InstallWiringOptions = {
  entitlementOverride?: Entitlement;

  /**
   * If true, allow re-install even if already installed (dev only use).
   * Default false.
   */
  force?: boolean;

  /**
   * If true, include full wiring manifest on wiring_ready event.
   * Recommended false by default; count-only manifestSummary is sufficient.
   */
  emitFullManifest?: boolean;

  /**
   * Heartbeat interval (ms). Default 10s.
   */
  heartbeatMs?: number;
};

function normalizeTier(tier: string | undefined): InstallContextV1["tier"] {
  if (tier === "public" || tier === "creator" || tier === "enterprise" || tier === "regulated") {
    return tier;
  }
  return "unknown";
}

function normalizeEnv(env: string | undefined): InstallContextV1["env"] {
  if (env === "prod" || env === "staging" || env === "dev") return env;
  return "unknown";
}

const TIER_RANK: Record<CapabilityTier, number> = {
  public: 0,
  creator: 1,
  enterprise: 2,
  regulated: 3,
};

function tierAllows(runtimeTier: InstallContextV1["tier"], required: CapabilityTier): boolean {
  if (runtimeTier === "unknown") return false;
  return TIER_RANK[runtimeTier] >= TIER_RANK[required];
}

function manifestsToDescriptors(manifests: CapabilityManifest[]): CapabilityDescriptorV1[] {
  // Deterministic: sort by manifest id before mapping
  const ordered = [...manifests].sort((a, b) => a.id.localeCompare(b.id));

  return ordered.map((m) => ({
    id: m.id,
    tierRequired: m.tier,
    safety: m.safety,
    requires: m.requires,
  }));
}

export function installWiring(opts?: InstallWiringOptions): () => void {
  const w = window as any;

  // Singleton guard: prevents accidental double-install (esp. during HMR / remounts)
  if (w.__synk_wiring_installed && !opts?.force) {
    report("wiring.install_skipped", { reason: "already_installed" });
    return w.__synk_wiring_uninstall ?? (() => {});
  }

  const entitlement = opts?.entitlementOverride ?? resolveEntitlement();
  const runtimeTier = normalizeTier(entitlement?.tier);

  const ctx: InstallContextV1 = {
    buildId: typeof w.__synk_build === "string" && w.__synk_build ? w.__synk_build : "unknown",
    env: normalizeEnv(w.__synk_env),
    tier: runtimeTier,
    authoritySource: (() => {
      const a = w.__synk_authority;
      if (a && typeof a === "object") {
        const kind = a.kind === "user" || a.kind === "service" ? a.kind : "unknown";
        const id = typeof a.id === "string" && a.id ? a.id : "unknown";
        return { kind, id };
      }
      return { kind: "unknown" as const, id: "unknown" };
    })(),
  };

  const all = getAllManifests();

  // Tier-gated selection (deterministic order preserved by registry + sort below)
  const selected = all
    .filter((m) => tierAllows(runtimeTier, m.tier))
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id));

  report("wiring.install_start", {
    tier: ctx.tier,
    manifestCount: all.length,
    selectedCount: selected.length,
  });

  // 1) Install capability side effects (collect uninstallers)
  const manifestUninstalls: Array<() => void> = [];
  for (const m of selected) {
    try {
      const un = m.install();
      manifestUninstalls.push(un);
      report("wiring.capability_installed", { id: m.id, tier: m.tier, safety: m.safety, version: m.version });
    } catch {
      // Keep going; individual capability failures should be surfaced via ops violations later.
      report("wiring.capability_install_failed", { id: m.id });
    }
  }

  // 2) Emit ops install pass (wiring_ready + capability events + heartbeat)
  const descriptors = manifestsToDescriptors(selected);
  const uninstallOpsSpine = installCapabilities(ctx, descriptors, {
    emitFullManifest: opts?.emitFullManifest ?? false,
    includeLabels: false, // best practice: avoid leaking names in manifest
    heartbeatMs: opts?.heartbeatMs ?? 10_000,
  });

  // 3) Compose uninstall (reverse order is usually safest)
  const uninstallAll = () => {
    try {
      uninstallOpsSpine();
    } catch {}

    for (let i = manifestUninstalls.length - 1; i >= 0; i--) {
      try {
        manifestUninstalls[i]?.();
      } catch {}
    }
  };

  // mark installed + expose uninstall for devtools
  w.__synk_wiring_installed = true;
  w.__synk_wiring_uninstall = () => {
    try {
      uninstallAll();
    } finally {
      // Clear markers so a future install is allowed
      try {
        delete w.__synk_wiring_installed;
        delete w.__synk_wiring_uninstall;
      } catch {
        // ignore
      }
      report("wiring.uninstalled", {});
    }
  };

  report("wiring.install_done", {
    tier: ctx.tier,
    manifestCount: all.length,
    selectedCount: selected.length,
  });

  return () => {
    try {
      w.__synk_wiring_uninstall?.();
    } catch {
      // ignore
    }
  };
}


